import type { IsoDate, Measurement } from "./types.js";
import type { SeasonBaseline } from "./plan.js";
import { TARGET_END_WEIGHT } from "./plan.js";
import { average } from "./numbers.js";
import { avgLast, previousAvg, linearPrediction, latestMeasurement } from "../../shared/measure-math.js";

/**
 * 平均、最新測量與線性外推的唯一來源在 `shared/measure-math.ts`，後端也讀
 * 同一份——監督者的升降方向由伺服器算，而 `functions/` 編譯不到 `src/`。
 * 這個檔案留下的是只有畫面用得到的那幾支，以及把它們收成一組的 `weightTrend()`。
 */
export type { MeasurementKey, Prediction } from "../../shared/measure-math.js";
export {
  avgLast, previousAvg, linearPrediction, latestMeasurement, recentMeasurements
} from "../../shared/measure-math.js";

import type { Prediction } from "../../shared/measure-math.js";

/**
 * 這一季的體重起點：上一季最後一筆有體重的量測。
 *
 * 通常就是生日目標日當天的結算體重；那天沒量就往前取。上一季一筆都讀不到時回
 * null，呼叫端要自己處理，不要編一個數字。
 */
export function seasonBaseline(previousSeason: Measurement[]): SeasonBaseline | null {
  const last = previousSeason
    .filter(row => Number.isFinite(row.weight_kg))
    .sort((a, b) => a.measurement_date.localeCompare(b.measurement_date))
    .at(-1);
  return last ? { date: last.measurement_date, weight: last.weight_kg as number } : null;
}

/** rollingAverage 會加上 `${key}_avg${window}` 這種動態欄位。 */
export type RollingRow<T> = T & { [avgKey: string]: unknown };

export function measurementMap<T extends { measurement_date: IsoDate }>(
  measurements: T[]
): Record<IsoDate, T> {
  return Object.fromEntries(measurements.map(item => [item.measurement_date, item]));
}

/**
 * 滾動平均。資料點不足時該點為 null——至少要 3 筆（或 window 較小者）
 * 才開始給值，避免頭幾天的平均線抖動得沒有意義。
 *
 * 對 row 的型別是泛型的：遮蔽後的 `ShapelessRow` 欄位不叫 `weight_kg`
 * （見 `measurement-view.ts`），綁死 `Measurement` 的話那一支就過不去。
 * 動態欄位的命名規則 `${key}_avg${window}` 不變，圖例與 `scaleless` 兩組
 * 測試依賴它。
 */
export function rollingAverage<T extends { measurement_date: IsoDate }>(
  rows: T[],
  key: keyof T & string,
  window: number
): RollingRow<T>[] {
  return rows.map((row, index) => {
    const slice = rows
      .slice(Math.max(0, index - window + 1), index + 1)
      .map(item => item[key] as number | null)
      .filter((value): value is number => Number.isFinite(value as number));
    return {
      ...row,
      [`${key}_avg${window}`]: slice.length >= Math.min(3, window) ? average(slice) : null
    } as RollingRow<T>;
  });
}

export interface WeightTrend {
  /** 最新一筆有體重的測量。沒有測量是 null。 */
  latest: Measurement | null;
  latestDate: IsoDate | null;
  /** 最新體重。CONTEXT.md 的「目前體重」。 */
  weight: number | null;
  /**
   * 前一筆**有體重**的測量日。只有一筆（或都沒有）是 null。
   *
   * 不是「最新測量日減一天」——那天可能根本沒量，寫成日期減一天的話會拿到
   * 一個不存在的比較基準（同 `shared/trend-direction.ts` 的 `dailyDirections`）。
   */
  previousDate: IsoDate | null;
  /** 最新體重與前一筆測量的差。正數是上升。只有一筆是 null。 */
  dayChange: number | null;
  /** 距離生日目標體重還差幾公斤。正數是還沒到。 */
  gapToTarget: number | null;
  /** 7 日平均體重。判讀趨勢的預設單位，不看單日。 */
  avg7: number | null;
  /** 本週變化：7 日平均與前一個 7 日平均的差。 */
  weeklyChange: number | null;
  avgFat7: number | null;
  /** 28 日平均淨體重。**不是骨骼肌量**，見 CONTEXT.md。 */
  avgLean28: number | null;
  leanChange: number | null;
  /** 依最近 28 日線性趨勢外推到生日目標日。資料不足是 null。 */
  prediction: Prediction | null;
}

/**
 * 趨勢摘要：判讀體重進度需要的那一整組數字。
 *
 * 上面那些函式（avgLast / previousAvg / linearPrediction …）是這個模組的
 * 內部零件——它們仍然 export，特徵測試直接測得到，但**畫面不該再自己組合
 * 它們**。先前統計頁、分享頁、文字戰報與 statusAssessment 各抄了一份同樣的
 * 八行食譜，其中一份還把 today 與 planDate 混用了。
 *
 * 與 src/domain/ 的其他模組一樣不讀系統時間：「今天」由呼叫端傳入。
 */
export function weightTrend(measurements: Measurement[], today: IsoDate): WeightTrend {
  const latest = latestMeasurement(measurements);
  const weight = latest ? (latest.weight_kg as number) : null;
  const previous = latestMeasurement(
    measurements.filter(item => latest !== null && item.measurement_date < latest.measurement_date)
  );
  const previousWeight = previous ? (previous.weight_kg as number) : null;

  const avg7 = avgLast(measurements, "weight_kg", 7);
  const avg7Prev = previousAvg(measurements, "weight_kg", 7);
  const avgLean28 = avgLast(measurements, "lean_body_mass_kg", 28);
  const avgLean28Prev = previousAvg(measurements, "lean_body_mass_kg", 28);

  return {
    latest,
    latestDate: latest ? latest.measurement_date : null,
    weight,
    previousDate: previous ? previous.measurement_date : null,
    dayChange: weight === null || previousWeight === null ? null : weight - previousWeight,
    gapToTarget: weight === null ? null : weight - TARGET_END_WEIGHT,
    avg7,
    weeklyChange: avg7 !== null && avg7Prev !== null ? avg7 - avg7Prev : null,
    avgFat7: avgLast(measurements, "body_fat_percent", 7),
    avgLean28,
    leanChange: avgLean28 !== null && avgLean28Prev !== null ? avgLean28 - avgLean28Prev : null,
    prediction: linearPrediction(measurements, today)
  };
}
